import { isUnusableParamValue } from './urlPassthrough'

// Resolves the Media Matchers publisher identification params from the URL.
//
// Rules (per partner spec):
//  - If `mPubGroupID` is present in the URL, use it (ignore mPubID/mPubCampaignID).
//  - Else if BOTH `mPubID` and `mPubCampaignID` are present, use those.
//  - Otherwise fall back to the call site's existing/default publisher params.
//
// Either `mPubGroupID` is sent, OR the `mPubID` + `mPubCampaignID` pair — never
// a mix. Pass the current `location.search` (or window.location.search) plus the
// call site's default params; spread the result into the outgoing query.
export function getPublisherParams(
  search: string,
  fallback: Record<string, string>,
): Record<string, string> {
  const params = new URLSearchParams(search)
  // A key that is empty ("&mPubGroupID=") or still an unsubstituted macro
  // ("&mPubGroupID={{pub}}") counts as absent, so the next rule applies and the
  // call site's default is used rather than junk being sent.
  const read = (name: string): string => {
    const value = params.get(name)
    return isUnusableParamValue(value) ? '' : (value as string).trim()
  }
  const mPubGroupID = read('mPubGroupID')
  if (mPubGroupID) {
    return { mPubGroupID }
  }
  const mPubID = read('mPubID')
  const mPubCampaignID = read('mPubCampaignID')
  if (mPubID && mPubCampaignID) {
    return { mPubID, mPubCampaignID }
  }
  // Drop any blank defaults so the call site can't emit an empty key either.
  return Object.fromEntries(
    Object.entries(fallback).filter(([, value]) => (value || '').trim()),
  )
}
